import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AlertColor } from "@mui/material";
import { ReactNode } from "react";

export type DialogActionState = {
  type:
    | "inherit"
    | "success"
    | "info"
    | "warning"
    | "error"
    | "accent"
    | "primary"
    | "secondary"
    | "lightPrimary"
    | undefined;
  title: string;
};

export type DialogActionInput = DialogActionState & {
  onClick?: () => void;
};

export type DialogState = {
  isVisible?: boolean;
  title?: string;
  description?: ReactNode;
  type?: "primary" | "error" | "secondary";
  actions?: DialogActionState[];
};

type ToastState = {
  isVisible?: boolean;
  message?: string;
  type?: AlertColor;
};

export type ShowDialogPayload = Omit<DialogState, "actions"> & {
  actions?: DialogActionInput[];
};

type AlertState = {
  dialog: DialogState;
  toast: ToastState;
};

const dialogActionHandlers: Array<(() => void) | undefined> = [];

export function getDialogActionHandler(
  index: number
): (() => void) | undefined {
  return dialogActionHandlers[index];
}

const initialState: AlertState = {
  dialog: {
    isVisible: false,
    title: "",
    description: "",
    type: "primary",
    actions: [],
  },
  toast: {
    isVisible: false,
    message: "",
    type: "success",
  },
};

const alertSlice = createSlice({
  name: "alert",
  initialState,
  reducers: {
    showDialog: {
      reducer: (
        state,
        action: PayloadAction<
          Omit<ShowDialogPayload, "actions"> & { actions?: DialogActionState[] }
        >
      ) => {
        state.dialog = {
          ...action.payload,
          isVisible: true,
        };
      },
      prepare: (payload: ShowDialogPayload) => {
        dialogActionHandlers.length = 0;
        const actions = payload.actions?.map((dialogAction, index) => {
          dialogActionHandlers[index] = dialogAction.onClick;
          return {
            type: dialogAction.type,
            title: dialogAction.title,
          };
        });

        return {
          payload: {
            title: payload.title,
            description: payload.description,
            type: payload.type,
            actions,
          },
        };
      },
    },
    hideDialog: (state) => {
      state.dialog.isVisible = false;
      dialogActionHandlers.length = 0;
    },
    showToast: (state, action: PayloadAction<AlertState["toast"]>) => {
      state.toast = { ...action.payload };
      state.toast.isVisible = true;
    },

    hideToast: (state) => {
      state.toast.isVisible = false;
    },
  },
});

export const { showDialog, hideDialog, showToast, hideToast } =
  alertSlice.actions;

export default alertSlice.reducer;
